import { Box, Button, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import {
    Add as AddIcon,
    SmartToyOutlined as AutomationIcon,
    HistoryOutlined as LogsIcon,
    LinkOutlined as InstagramIcon,
    HelpOutlineOutlined as HelpIcon,
    CardMembershipOutlined as SubscriptionIcon,
} from '@mui/icons-material';
import { ACCENT, ACCENT_HOVER, ACCENT_TINT, ACCENT_TINT_STRONG } from '@/pages/AutoDm/_shared';

export type DmSection = 'list' | 'logs' | 'account' | 'subscription' | 'help';

interface Props {
    active: DmSection;
    onChange: (section: DmSection) => void;
    onCreate: () => void;
}

interface Item {
    key: DmSection;
    label: string;
    icon: React.ReactNode;
}

const ITEMS: Item[] = [
    { key: 'list', label: '자동화 관리', icon: <AutomationIcon fontSize="small" /> },
    { key: 'logs', label: '보낸 기록', icon: <LogsIcon fontSize="small" /> },
    { key: 'account', label: '인스타 연결', icon: <InstagramIcon fontSize="small" /> },
    { key: 'subscription', label: '구독', icon: <SubscriptionIcon fontSize="small" /> },
    { key: 'help', label: '도움말', icon: <HelpIcon fontSize="small" /> },
];

const AutoDmSidebar = ({ active, onChange, onCreate }: Props) => {
    return (
        <Box
            component="aside"
            sx={{
                width: 220,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                py: 2.5,
                px: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
            }}
        >
            <Typography
                variant="overline"
                sx={{ px: 1.5, pb: 1, color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
            >
                자동DM
            </Typography>

            <Button
                onClick={onCreate}
                variant="contained"
                startIcon={<AddIcon />}
                disableElevation
                sx={{
                    bgcolor: ACCENT,
                    color: '#fff',
                    fontWeight: 700,
                    justifyContent: 'flex-start',
                    px: 1.75,
                    py: 1.25,
                    mb: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    cursor: 'pointer',
                    transition: 'transform 120ms ease, background-color 120ms ease',
                    willChange: 'transform',
                    '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)' },
                }}
            >
                새 자동화 만들기
            </Button>

            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {ITEMS.map((item) => {
                    const isActive = active === item.key;
                    return (
                        <ListItemButton
                            key={item.key}
                            onClick={() => onChange(item.key)}
                            disableGutters
                            sx={{
                                px: 1.5,
                                py: 1.25,
                                borderRadius: 2,
                                cursor: 'pointer',
                                color: isActive ? ACCENT : 'text.secondary',
                                bgcolor: isActive ? ACCENT_TINT : 'transparent',
                                transition: 'background-color 120ms ease, color 120ms ease',
                                willChange: 'background-color',
                                '&:hover': {
                                    bgcolor: isActive ? ACCENT_TINT_STRONG : 'action.hover',
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>{item.icon}</ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: isActive ? 700 : 500,
                                }}
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        </Box>
    );
};

export default AutoDmSidebar;
